import { t } from 'i18next'
import { ToastEventPayload } from '~/hooks/toast/useToastAlerts'
import {
  NotificationDomain,
  NotificationPayload,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'

export enum WebphoneOperation {
  CallDeclined = 'call_declined',
  CallEnded = 'call_ended',
  CallHeld = 'call_held',
  CallParked = 'call_parked',
  CallTransferred = 'call_transferred',
}

export type WebphoneOperationContext = {
  [WebphoneOperation.CallDeclined]: string
  [WebphoneOperation.CallEnded]: string
  [WebphoneOperation.CallHeld]: string
  [WebphoneOperation.CallParked]: { callName: string; parkingLotName: string }
  [WebphoneOperation.CallTransferred]: {
    callName: string
    transferToName: string
  }
}

const getCallDeclinedToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  let title = ''
  let showToast = true
  const context =
    payload.context as WebphoneOperationContext[WebphoneOperation.CallDeclined]

  switch (payload.disposition) {
    case 'error':
      title = t('call.toast.callDeclined', { context })
      break
    default: {
      showToast = false
      break
    }
  }

  if (!showToast) {
    return
  }

  return {
    title,
    disposition: payload.disposition,
    durationMs: 3250,
  } as ToastEventPayload
}

const getCallEndedToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  let title = ''
  let showToast = true
  const context =
    payload.context as WebphoneOperationContext[WebphoneOperation.CallEnded]

  switch (payload.disposition) {
    case 'error':
      title = t('call.toast.callEnded', { context })
      break
    default: {
      showToast = false
      break
    }
  }

  if (!showToast) {
    return
  }

  return {
    title,
    disposition: payload.disposition,
    durationMs: 3250,
  } as ToastEventPayload
}

const getCallHeldToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  let title = ''
  let showToast = true
  const context =
    payload.context as WebphoneOperationContext[WebphoneOperation.CallHeld]

  switch (payload.disposition) {
    case 'success':
      title = t('call.toast.callHeld', { context })
      break
    default: {
      showToast = false
      break
    }
  }

  if (!showToast) {
    return
  }

  return {
    title,
    disposition: payload.disposition,
    durationMs: 3250,
  } as ToastEventPayload
}

const getCallParkedToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  let title = ''
  let showToast = true
  const { callName, parkingLotName } =
    payload.context as WebphoneOperationContext[WebphoneOperation.CallParked]

  switch (payload.disposition) {
    case 'success':
      title = t('call.toast.callParked', { callName, parkingLotName })
      break
    default: {
      showToast = false
      break
    }
  }

  if (!showToast) {
    return
  }

  return {
    title,
    disposition: payload.disposition,
    durationMs: 3250,
  } as ToastEventPayload
}

const getCallTransferredToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  let title = ''
  let showToast = true
  const { callName, transferToName } =
    payload.context as WebphoneOperationContext[WebphoneOperation.CallTransferred]

  switch (payload.disposition) {
    case 'success':
      title = t('call.toast.callTransferred', { callName, transferToName })
      break
    default: {
      showToast = false
      break
    }
  }

  if (!showToast) {
    return
  }

  return {
    title,
    disposition: payload.disposition,
    durationMs: 3250,
  } as ToastEventPayload
}

export const getWebphoneToastEvent = (
  payload: NotificationPayload<NotificationDomain.WEBPHONE>,
) => {
  switch (payload.operation) {
    case WebphoneOperation.CallDeclined:
      return getCallDeclinedToastEvent(payload)
    case WebphoneOperation.CallEnded:
      return getCallEndedToastEvent(payload)
    case WebphoneOperation.CallHeld:
      return getCallHeldToastEvent(payload)
    case WebphoneOperation.CallParked:
      return getCallParkedToastEvent(payload)
    case WebphoneOperation.CallTransferred:
      return getCallTransferredToastEvent(payload)
    default:
      return null
  }
}
